import { Box, Button, Flex, useToast } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useCreateInvoiceContext } from '../../hooks/useCreateInvoice';
import { InvoiceDetailInformation } from '../InvoiceDetailInformation';
import {
  CreateInvoiceHeaderWithTimelineStepper,
  CreateInvoiceStepIndex,
} from './CreateInvoiceHeaderWithTimelineStepper';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import {
  GetInvoicesByOrganizationIdDocumentGQLType,
  useCreateInvoiceWithInvoiceItemsMutation,
} from 'packages/invoices/requests/invoices.gql.generated';
import { GetResolvedLoanByIdDocumentGQLType } from 'packages/loans/requests/loans.gql.generated';

export const CreateInvoiceReview = ({
  setCurrentStepIndex,
}: {
  setCurrentStepIndex: React.Dispatch<
    React.SetStateAction<CreateInvoiceStepIndex>
  >;
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const {
    invoice,
    invoiceComponentsWithInvoiceItems,
    selectedEntities: {
      loan: { loan },
      creditFacility: { creditFacility },
      bankAccounts: { destinationBankAccount },
    },
  } = useCreateInvoiceContext();

  const [
    createInvoiceWithInvoiceItems,
    { loading: isCreateInvoiceLoading },
  ] = useCreateInvoiceWithInvoiceItemsMutation({
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'Unable to create invoice. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    refetchQueries: [
      GetInvoicesByOrganizationIdDocumentGQLType,
      GetResolvedLoanByIdDocumentGQLType,
    ],
  });

  const onClickCreateInvoice = useCallback(async () => {
    const response = await createInvoiceWithInvoiceItems({
      variables: {
        invoice,
        invoiceComponentsWithInvoiceItems,
      },
    });

    if (response.errors) {
      toast({
        ...errorToastConfig,
        title: 'Unable to create invoice.',
      });

      return;
    }

    if (response.data) {
      toast({ ...successToastConfig, title: 'Successfully created invoice' });

      const baseInvoiceDetailPath = `/invoices/${response.data.createInvoiceWithInvoiceItems.invoiceId}`;

      if (response.data.createInvoiceWithInvoiceItems.invoiceWorkleaseId) {
        // if worklease is created, redirect to the invoice detail page with the worklease id
        navigate(
          `${baseInvoiceDetailPath}/worklease/${response.data.createInvoiceWithInvoiceItems.invoiceWorkleaseId}`,
        );
      } else {
        // otherwise, redirect to the invoice detail page without the worklease id
        navigate(baseInvoiceDetailPath);
      }
    }
  }, [
    createInvoiceWithInvoiceItems,
    invoice,
    invoiceComponentsWithInvoiceItems,
    navigate,
    toast,
  ]);

  return (
    <Flex direction="column" h="100vh">
      {/* Header */}
      <Box mb="40px">
        <CreateInvoiceHeaderWithTimelineStepper completedStepIndex={3} />
      </Box>

      <Flex direction="column" flexGrow={1}>
        {creditFacility && (
          <InvoiceDetailInformation
            invoice={{
              ...invoice,
              invoiceComponentsWithInvoiceItems,
              creditFacility: {
                id: creditFacility.id,
                name: creditFacility.name,
              },
              loan,
              destinationBankAccount: destinationBankAccount
                ? (({ __typename: _typename, ...rest }) => rest)(
                    destinationBankAccount,
                  )
                : undefined,
              paymentSourceOrganization: {
                id: creditFacility.borrowerId,
                name: creditFacility.borrowerEntityName,
              },
            }}
            isPreview
          />
        )}
      </Flex>

      {/* Form Navigation */}
      <Flex justifyContent="space-between" py="40px">
        <Button
          variant="v2-ghost"
          onClick={() => setCurrentStepIndex(2)}
          isDisabled={isCreateInvoiceLoading}
        >
          Back
        </Button>
        <Button
          variant="primary"
          colorScheme="v2-aurora"
          isLoading={isCreateInvoiceLoading}
          onClick={onClickCreateInvoice}
        >
          Create Invoice
        </Button>
      </Flex>
    </Flex>
  );
};
